import { isObject } from 'lodash-es';

export const DEFAULT_MASK_VALUE = '********';

/**
 * Traverse the object and mask any key value defined in `keys`
 * @param obj operation variables
 * @param keys array of key names to mask
 * @param maskedValue the value to replace with (default: '********')
 */
export const maskValuesInObject = <T extends Record<string, unknown>>(
  obj: T,
  keys: string[],
  maskedValue: string = DEFAULT_MASK_VALUE
): T => {
  const visited = new WeakSet<object>();

  const traverse = (value: any, key?: string): any => {
    if (key && keys.includes(key)) {
      return maskedValue;
    }

    if (Array.isArray(value)) {
      if (visited.has(value)) {
        return '[Circular]';
      }

      visited.add(value);

      return value.map(item => traverse(item));
    }

    if (isObject(value)) {
      if (visited.has(value)) {
        return '[Circular]';
      }

      visited.add(value);

      const result: Record<string, any> = {};

      for (const [objectKey, value_] of Object.entries(value)) {
        result[objectKey] = traverse(value_, objectKey);
      }

      return result;
    }

    return value;
  };

  return traverse(obj) as T;
};

/**
 * Check if an object is contained within another object.
 *
 * Returns `true` if:
 * - all enumerable keys of *subset* are also enumerable in *superset*, and
 * - every value assigned to an enumerable key of *subset* strictly equals
 *   the value assigned to the same key of *superset* – or is a subset of it.
 *
 * @param  {Object}  superset
 * @param  {Object}  subset
 *
 * @returns  {Boolean}
 *
 * @function  default
 * @alias     isSubset
 */

export const isSubset = (superset: Record<string, any>, subset: Record<string, any>): boolean => {
  if (typeof superset !== 'object' || superset === null || typeof subset !== 'object' || subset === null) {
    return false;
  }

  if (superset instanceof Date || subset instanceof Date) {
    return superset.valueOf() === subset.valueOf();
  }

  return Object.keys(subset).every(key => {
    if (!superset.propertyIsEnumerable(key)) {
      return false;
    }

    const subsetItem = subset[key];
    const supersetItem = superset[key];
    if (
      typeof subsetItem === 'object' && subsetItem !== null
        ? !isSubset(supersetItem, subsetItem)
        : supersetItem !== subsetItem
    ) {
      return false;
    }

    return true;
  });
};

export const isFile = (input: unknown): input is File =>
  isObject(input) && 'File' in globalThis && input instanceof File;
